import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { workerTeamsAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Users,
  Plus,
  X,
  MapPin,
  CurrencyDollar,
  Eye,
  EyeSlash,
  SpinnerGap,
  ArrowLeft,
  Lightbulb
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const CreateWorkerTeam = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    skills: [],
    visibility: 'public',
    pricing_type: 'individual',
    team_rate: '',
    location: '',
    availability: ''
  });
  const [newSkill, setNewSkill] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Team name is required',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.description.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Team description is required',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formData,
        team_rate: formData.pricing_type === 'team_rate' && formData.team_rate 
          ? parseFloat(formData.team_rate) 
          : null
      };
      
      const response = await workerTeamsAPI.create(payload);
      
      if (response.data.success) {
        toast({
          title: 'Team Created!',
          description: 'Your team has been created successfully'
        });
        navigate(`/worker-teams/${response.data.team.id}`);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create team',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="create-worker-team-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-8 border-b-4 border-black">
        <div className="max-w-3xl mx-auto px-4">
          <button 
            onClick={() => navigate('/worker-teams')}
            className="flex items-center gap-2 text-blue-100 hover:text-white mb-4 font-bold"
          >
            <ArrowLeft size={20} weight="bold" />
            Back to My Teams
          </button>
          <h1 className="text-3xl md:text-4xl font-black">Create a Team</h1>
          <p className="text-blue-100 text-lg mt-2">Build your crew and take on bigger projects together</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Team Name */}
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
            <Label htmlFor="name" className="text-lg font-black text-gray-900 mb-2 block">
              Team Name *
            </Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Elite Electrical Crew"
              className="h-14 text-lg border-2 border-black rounded-xl"
              data-testid="team-name-input"
            />
          </div>

          {/* Description */}
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
            <Label htmlFor="description" className="text-lg font-black text-gray-900 mb-2 block">
              Description *
            </Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Tell contractors about your team's expertise, experience, and what makes you great..."
              className="min-h-[120px] text-lg border-2 border-black rounded-xl"
              data-testid="team-description-input"
            />
          </div>

          {/* Skills */}
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
            <Label className="text-lg font-black text-gray-900 mb-2 block">
              Team Skills
            </Label>
            <p className="text-sm text-gray-500 mb-4">Add skills your team specializes in</p>
            <div className="flex gap-2 mb-4">
              <Input
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                placeholder="e.g., Electrical, Plumbing"
                className="h-12 border-2 border-gray-300 rounded-xl"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                data-testid="skill-input"
              />
              <Button 
                type="button" 
                onClick={addSkill}
                className="h-12 px-4 bg-[#2B4C6F] border-2 border-black"
              >
                <Plus size={20} weight="bold" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.skills.map((skill, idx) => (
                <Badge 
                  key={idx} 
                  variant="secondary" 
                  className="px-3 py-2 text-sm flex items-center gap-2"
                >
                  {skill}
                  <button 
                    type="button"
                    onClick={() => removeSkill(skill)} 
                    className="hover:text-red-500"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </Badge>
              ))}
              {formData.skills.length === 0 && (
                <p className="text-sm text-gray-400">No skills added yet</p>
              )}
            </div>
          </div>

          {/* Location & Availability */}
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="location" className="text-lg font-black text-gray-900 mb-2 block">
                  <MapPin size={20} weight="bold" className="inline mr-2" />
                  Location
                </Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g., Los Angeles, CA"
                  className="h-12 border-2 border-gray-300 rounded-xl"
                  data-testid="location-input"
                />
              </div>
              <div>
                <Label htmlFor="availability" className="text-lg font-black text-gray-900 mb-2 block">
                  Availability
                </Label>
                <Input
                  id="availability"
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  placeholder="e.g., Weekdays, Full-time"
                  className="h-12 border-2 border-gray-300 rounded-xl"
                  data-testid="availability-input"
                />
              </div>
            </div>
          </div>

          {/* Visibility */}
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
            <Label className="text-lg font-black text-gray-900 mb-4 block">
              Team Visibility
            </Label>
            <div className="grid md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, visibility: 'public' }))}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  formData.visibility === 'public'
                    ? 'border-[#2B4C6F] bg-[#2B4C6F]/10'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="visibility-public"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Eye size={24} weight="bold" className={formData.visibility === 'public' ? 'text-[#2B4C6F]' : 'text-gray-400'} />
                  <span className="font-bold text-gray-900">Public</span>
                </div>
                <p className="text-sm text-gray-500">Contractors can find and contact your team</p>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, visibility: 'private' }))}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  formData.visibility === 'private'
                    ? 'border-[#2B4C6F] bg-[#2B4C6F]/10'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="visibility-private"
              >
                <div className="flex items-center gap-3 mb-2">
                  <EyeSlash size={24} weight="bold" className={formData.visibility === 'private' ? 'text-[#2B4C6F]' : 'text-gray-400'} />
                  <span className="font-bold text-gray-900">Private</span>
                </div>
                <p className="text-sm text-gray-500">Only visible when you apply to jobs</p>
              </button>
            </div>
          </div>

          {/* Pricing Type */}
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
            <Label className="text-lg font-black text-gray-900 mb-4 block">
              <CurrencyDollar size={20} weight="bold" className="inline mr-2" />
              Pricing Model
            </Label>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, pricing_type: 'individual' }))}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  formData.pricing_type === 'individual'
                    ? 'border-[#2B4C6F] bg-[#2B4C6F]/10'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="pricing-individual"
              >
                <span className="font-bold text-gray-900">Individual Rates</span>
                <p className="text-sm text-gray-500 mt-1">Show each member's hourly rate</p>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, pricing_type: 'team_rate' }))}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  formData.pricing_type === 'team_rate'
                    ? 'border-[#2B4C6F] bg-[#2B4C6F]/10'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="pricing-team"
              >
                <span className="font-bold text-gray-900">Team Rate</span>
                <p className="text-sm text-gray-500 mt-1">Single rate for the whole team</p>
              </button>
            </div>
            {formData.pricing_type === 'team_rate' && (
              <div>
                <Label htmlFor="team_rate" className="font-bold text-gray-700 mb-2 block">
                  Team Hourly Rate ($)
                </Label>
                <Input
                  id="team_rate"
                  name="team_rate"
                  type="number"
                  value={formData.team_rate}
                  onChange={handleChange}
                  placeholder="e.g., 250"
                  className="h-12 border-2 border-gray-300 rounded-xl max-w-xs"
                  data-testid="team-rate-input"
                />
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <Lightbulb size={24} weight="fill" className="text-amber-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-amber-800 mb-2">Tips for a Great Team Profile</h3>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Add relevant skills to help contractors find your team</li>
                  <li>• Write a compelling description highlighting your experience</li>
                  <li>• Include your service area in the location field</li>
                  <li>• After creating, invite skilled workers to join your team</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/worker-teams')}
              className="h-14 px-8 font-bold text-lg rounded-xl border-2 border-black"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="h-14 px-8 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none flex-1"
              data-testid="create-team-submit"
            >
              {loading ? (
                <><SpinnerGap size={24} className="animate-spin mr-2" /> Creating...</>
              ) : (
                <><Users size={24} weight="bold" className="mr-2" /> Create Team</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWorkerTeam;
